import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import * as THREE from "three";
import { TransformControls } from "three/addons/controls/TransformControls.js";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { GTAOPass } from "three/addons/postprocessing/GTAOPass.js";
import { SMAAPass } from "three/addons/postprocessing/SMAAPass.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";
import { Pass } from "three/addons/postprocessing/Pass.js";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/shadcn/select";
import {
  buildSceneGraph,
  disposeSceneGraph,
  HELPER_LAYER,
  DEFAULT_Z_DEPTH_DISTANCE,
} from "./sceneGraph";
import {
  resolveProjectionDescriptors,
  renderProjectorDepth,
  pruneProjectorDepthTargets,
} from "./projection";
import {
  runAIRenderPipeline,
  buildSemanticGroups,
  buildSemanticLights,
  computeObjectBounds,
  computeObjectBoundsById,
  computeLightBounds,
  computeLightBoundsById,
  buildEnvironmentInfo,
} from "./renderPipeline";
import { resolveQualityPreset } from "./renderQuality";
import { isEqual, omit } from "lodash";

// Fields that never influence buildSceneGraph's output — only this editor's
// text-authoring UI (PropertiesPanel's AI tab, HierarchyPanel's row label)
// reads them. Excluded from the rebuild-relevance comparison below so typing
// into one of them (which calls setNodes on every keystroke) can't trigger
// the big rebuild effect's full scene-graph teardown/rebuild.
const NODE_FIELDS_IRRELEVANT_TO_REBUILD = [
  "name",
  "description",
  "aiDescription",
  "aiReferenceImages",
];

// While piloting a real scene camera, the render is contain-fit to that
// camera's own configured aspect and letterboxed/pillarboxed within whatever
// space the panel layout gives it (see `resize` below) instead of stretching
// to fill it — a perspective camera's horizontal FOV is derived from aspect ×
// vertical FOV, so letting the panel's raw, ever-changing w/h drive a piloted
// camera's `.aspect` directly made dragging the side-panel divider narrow or
// widen what that camera captures (a real framing/POV change), not just scale
// the same picture down. The free exploration camera is unaffected by this —
// it still always fills the available space, same as before. This is only
// the fallback for a camera node that doesn't specify its own `aspect`.
const DEFAULT_CAMERA_ASPECT = 16 / 9;
const CLICK_DRAG_THRESHOLD_PX = 5;
const RAD2DEG = 180 / Math.PI;
const DEG2RAD = Math.PI / 180;
const FLY_SPEED = 12; // world units / second, doubled with Shift held
const WHEEL_SPEED = 0.01; // world units per wheel-delta unit
const LOOK_SENSITIVITY = 0.0035; // radians per pixel of mouse drag
const PITCH_LIMIT = Math.PI / 2 - 0.01; // just under straight up/down, avoids gimbal flip
const ORBIT_SENSITIVITY = 0.0035; // radians per pixel of mouse drag
const ORBIT_POLAR_LIMIT = 0.05; // radians of headroom kept from the poles, avoids the orbit flipping over the top/bottom
const DEFAULT_ORBIT_DISTANCE = 8; // world units in front of the camera used to (re)center an unlocked orbit pivot
// TransformControls' own snap step while Shift is held during a gizmo drag —
// read live on every pointermove (not snapshotted at drag-start), so toggling
// these on Shift keydown/keyup takes effect mid-drag. rotationSnap is in
// radians; scaleSnap snaps the scale axis value itself to the nearest step
// (1, 1.05, 1.10, ...), not a percentage delta from the drag-start scale.
const SHIFT_TRANSLATION_SNAP = 5; // world units
const SHIFT_ROTATION_SNAP = DEG2RAD * 5; // 5 degrees
const SHIFT_SCALE_SNAP = 0.05;
// Arrow keys and WASD both fly the camera (forward/back/strafe) — held
// together they just add up to the same direction twice, no special-casing
// needed. Q/E fly straight up/down along world Y, freed up for movement now
// that the gizmo-mode hotkeys below live on the number row instead.
const MOVEMENT_KEYS = [
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "w",
  "a",
  "s",
  "d",
  "q",
  "e",
];
// The Depth Map pass's background — the background is empty space, i.e.
// further than anything the scene could ever put in front of the camera, so
// it always reads as the pass's own black/"0" end regardless of whatever
// color or texture World settings has configured for Shaded. One shared
// instance (THREE.Color is otherwise just read here, never mutated).
const DEPTH_MAP_BACKGROUND_COLOR = new THREE.Color(0x000000);
// On the number row rather than Q/W/E/R so those letters are free for WASD
// camera movement above.
const GIZMO_KEY_TO_MODE = {
  1: "select",
  2: "translate",
  3: "rotate",
  4: "scale",
};

const isTypingTarget = (el) =>
  !!el &&
  (["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) ||
    el.isContentEditable);

// event.key carries case for letters (Shift+w -> "W") but MOVEMENT_KEYS/
// GIZMO_KEY_TO_MODE are keyed lowercase — digits and multi-char names
// (ArrowUp, Shift) pass through untouched.
const normalizeKey = (key) => (key.length === 1 ? key.toLowerCase() : key);

// Three.js reads translationSnap/rotationSnap/scaleSnap fresh on every
// pointermove, so flipping them on Shift keydown/keyup (rather than only at
// drag-start) snaps a drag already in progress too. `null` is TransformControls'
// own "off" value (a falsy snap step disables snapping for that mode).
const setGizmoSnap = (transformControls, enabled) => {
  transformControls.translationSnap = enabled ? SHIFT_TRANSLATION_SNAP : null;
  transformControls.rotationSnap = enabled ? SHIFT_ROTATION_SNAP : null;
  transformControls.scaleSnap = enabled ? SHIFT_SCALE_SNAP : null;
};

const extractTransform = (object3d) => ({
  position: {
    x: object3d.position.x,
    y: object3d.position.y,
    z: object3d.position.z,
  },
  rotation: {
    x: object3d.rotation.x * RAD2DEG,
    y: object3d.rotation.y * RAD2DEG,
    z: object3d.rotation.z * RAD2DEG,
  },
  scale: { x: object3d.scale.x, y: object3d.scale.y, z: object3d.scale.z },
});

// Object3D.lookAt() special-cases `isCamera`/`isLight` to aim -Z at the
// target; anything else (e.g. the plain THREE.Group a piloted scene camera
// lives in) gets the reversed convention instead, aiming +Z — which pointed
// piloted cameras exactly backwards, since the real PerspectiveCamera nested
// inside that group still renders along its own -Z regardless of what
// convention its parent group was aimed with. This always uses the
// camera-style convention so orbit/focus behave the same for both.
const aimMatrix = new THREE.Matrix4();
const aimAt = (object3d, worldPoint) => {
  aimMatrix.lookAt(object3d.position, worldPoint, object3d.up);
  object3d.quaternion.setFromRotationMatrix(aimMatrix);
};

// TransformControls' stock translate/scale gizmos draw *two* arrow/box
// handles per axis (one on each side of the origin) — 3 axes, 6 visible
// handles — and orbiting the camera around the object regularly leaves one
// of each pair pointing straight away from the viewer. There's no public API
// for trimming that down, so this reaches into TransformControls' otherwise-
// undocumented internals (`_gizmo`, each handle mesh's `.name` plus its
// baked-in geometry offset) to show only whichever handle of each axis
// currently faces the camera, flipping as the camera orbits. It reuses the
// exact `eye`/`worldQuaternion` values TransformControls already recomputes
// every frame for its own axis-hide-when-edge-on logic (see
// AXIS_HIDE_THRESHOLD in TransformControls.js), rather than re-deriving them.
// Fragile to a three.js version bump (private `_gizmo`, the assumption that
// each axis has exactly 3 baked meshes) but degrades safely: if the shape
// doesn't match, installSingleArrowGizmo below just no-ops and the stock
// double-handle look stays.
const AXIS_UNIT = {
  X: new THREE.Vector3(1, 0, 0),
  Y: new THREE.Vector3(0, 1, 0),
  Z: new THREE.Vector3(0, 0, 1),
};
const AXIS_UNIT_BY_INDEX = [AXIS_UNIT.X, AXIS_UNIT.Y, AXIS_UNIT.Z];
const AXIS_COMPONENT_KEYS = ["x", "y", "z"];
const PLANE_NAMES = ["XY", "YZ", "XZ"];
const PLANE_OFFSET_EPSILON = 0.01; // well below a real baked offset (~0.15), well above float noise
const IDENTITY_QUATERNION = new THREE.Quaternion();
const singleArrowTempDir = new THREE.Vector3();
const singleArrowTempCenter = new THREE.Vector3();

// One-time, per axis: of the 3 baked meshes sharing that axis's name (two
// arrow/box handles plus one connecting shaft), figures out which is which
// from their pre-baked geometry offsets — `object.position` is reset to
// (0,0,0) right after baking (see TransformControlsGizmo's setupGizmo), so
// inspecting each mesh's own geometry is the only way to tell them apart.
function classifyAxisHandles(meshes) {
  const infos = meshes.map((mesh) => {
    mesh.geometry.computeBoundingBox();
    mesh.geometry.boundingBox.getCenter(singleArrowTempCenter);
    let axisIndex = 0;
    let offset = 0;
    AXIS_COMPONENT_KEYS.forEach((key, i) => {
      if (Math.abs(singleArrowTempCenter[key]) > Math.abs(offset)) {
        offset = singleArrowTempCenter[key];
        axisIndex = i;
      }
    });
    return { mesh, offset, axisIndex };
  });
  // The shaft's baked offset (~0.25, spanning origin to the arrow) is
  // reliably smaller in magnitude than either arrow/box's (~0.45-0.5).
  infos.sort((a, b) => Math.abs(a.offset) - Math.abs(b.offset));
  const [shaftInfo, a, b] = infos;
  return {
    positive: a.offset > 0 ? a.mesh : b.mesh,
    negative: a.offset > 0 ? b.mesh : a.mesh,
    shaft: shaftInfo.mesh,
    shaftAxisIndex: shaftInfo.axisIndex,
    shaftPositive: shaftInfo.offset >= 0,
  };
}

// Classifies every X/Y/Z axis of one gizmo group (translate or scale) —
// skips the XY/YZ/XZ plane handles (handled separately below) and the XYZ
// center handle, which doesn't have this double-handle problem.
function classifyGizmoGroup(group) {
  const byAxis = { X: [], Y: [], Z: [] };
  for (const mesh of group.children) {
    if (byAxis[mesh.name]) byAxis[mesh.name].push(mesh);
  }
  const result = {};
  for (const axis of ["X", "Y", "Z"]) {
    if (byAxis[axis].length === 3) {
      result[axis] = classifyAxisHandles(byAxis[axis]);
    }
  }
  return result;
}

// Every dominant (non-negligible) offset axis of a mesh's baked geometry —
// generalizes classifyAxisHandles' single-axis lookup to the XY/YZ/XZ plane
// handles, which bake in offsets on *two* axes at once (e.g. XY's box sits at
// local (0.15, 0.15, 0)). Confirmed empirically that a baked construction-time
// rotation (YZ, XZ) doesn't scramble this: a symmetric box's own center ends
// up exactly at its authored `position` regardless of `rotation`, so local
// axis index 0/1/2 always lines up with conceptual X/Y/Z here, same as the
// single-axis meshes.
function classifyOffsetAxes(mesh) {
  mesh.geometry.computeBoundingBox();
  mesh.geometry.boundingBox.getCenter(singleArrowTempCenter);
  const axes = [];
  AXIS_COMPONENT_KEYS.forEach((key, i) => {
    const value = singleArrowTempCenter[key];
    if (Math.abs(value) > PLANE_OFFSET_EPSILON) {
      axes.push({ axisIndex: i, sign: value >= 0 ? 1 : -1 });
    }
  });
  return axes;
}

// Pairs each plane handle's *visible* mesh with its invisible picker
// counterpart (same name, same baked offset, separate object — see
// pickerTranslate/pickerScale in TransformControls.js) so both get mirrored
// together; otherwise the clickable hit-zone would stay put while the square
// people actually see moves to the opposite quadrant.
function classifyPlaneHandles(gizmoGroup, pickerGroup) {
  const result = {};
  for (const name of PLANE_NAMES) {
    const visual = gizmoGroup.children.find((m) => m.name === name);
    const picker = pickerGroup.children.find((m) => m.name === name);
    if (!visual || !picker) continue;
    const axes = classifyOffsetAxes(visual);
    if (axes.length) result[name] = { visual, picker, axes };
  }
  return result;
}

function installSingleArrowGizmo(transformControls) {
  const gizmo = transformControls._gizmo;
  if (
    !gizmo?.gizmo?.translate ||
    !gizmo?.gizmo?.scale ||
    !gizmo?.picker?.translate ||
    !gizmo?.picker?.scale ||
    !gizmo?.helper?.translate ||
    !gizmo?.helper?.scale
  )
    return;

  const classified = {
    translate: classifyGizmoGroup(gizmo.gizmo.translate),
    scale: classifyGizmoGroup(gizmo.gizmo.scale),
  };
  const planeClassified = {
    translate: classifyPlaneHandles(
      gizmo.gizmo.translate,
      gizmo.picker.translate,
    ),
    scale: classifyPlaneHandles(gizmo.gizmo.scale, gizmo.picker.scale),
  };
  // The library draws a long guide line across the whole scene for whichever
  // axis is currently hovered or being dragged (see the generic 'helper'-tag
  // branch in TransformControlsGizmo's updateMatrixWorld, driven by
  // `this.axis.search(handle.name)`) — an edit-time aid we don't want, so
  // these are just force-hidden every frame instead of toggled.
  const hoverGuideLines = [
    ...gizmo.helper.translate.children,
    ...gizmo.helper.scale.children,
  ].filter((mesh) => ["X", "Y", "Z"].includes(mesh.name));

  const applyFacing = () => {
    for (const line of hoverGuideLines) line.visible = false;

    const modeClassified = classified[transformControls.mode];
    const modePlanes = planeClassified[transformControls.mode];
    if (!modeClassified && !modePlanes) return; // rotate mode — nothing to flip
    const space =
      transformControls.mode === "scale" ? "local" : transformControls.space;
    const quaternion =
      space === "local"
        ? transformControls.worldQuaternion
        : IDENTITY_QUATERNION;

    for (const axis of ["X", "Y", "Z"]) {
      const handles = modeClassified?.[axis];
      if (!handles) continue;
      // Already fully hidden by the library's own edge-on-to-camera check —
      // both sides stay hidden, nothing to flip.
      if (!handles.positive.visible && !handles.negative.visible) continue;
      singleArrowTempDir.copy(AXIS_UNIT[axis]).applyQuaternion(quaternion);
      const towardPositive = singleArrowTempDir.dot(transformControls.eye) >= 0;
      handles.positive.visible = towardPositive;
      handles.negative.visible = !towardPositive;
      if (towardPositive !== handles.shaftPositive) {
        const magnitude = Math.abs(
          handles.shaft.scale.getComponent(handles.shaftAxisIndex),
        );
        handles.shaft.scale.setComponent(handles.shaftAxisIndex, -magnitude);
        // originalUpdateMatrixWorld() above already computed this mesh's
        // matrixWorld (as part of its own trailing super.updateMatrixWorld()
        // recursing into every handle) — for the STOCK scale that already
        // happened before we ever touched it, so it's baked into what's
        // about to render. Mutating .scale afterwards doesn't retroactively
        // update matrixWorld; forcing it here is what actually moves the
        // shaft this frame instead of one frame late.
        handles.shaft.updateMatrixWorld(true);
      }
    }

    // Plane handles (XY/YZ/XZ) are a single mesh straddling two axes at
    // once — each axis flips independently, same eye/quaternion test as
    // above, just applied per-component instead of choosing between two
    // pre-existing meshes. The invisible picker mesh mirrors right along
    // with the visible one so the clickable zone never drifts away from
    // what's actually drawn.
    for (const name of PLANE_NAMES) {
      const plane = modePlanes?.[name];
      if (!plane) continue;
      // Already hidden by the library's own plane-hide-threshold (viewed
      // edge-on) — visual and picker are always kept in sync, so checking
      // one is enough.
      if (!plane.visual.visible) continue;
      let flipped = false;
      for (const { axisIndex, sign } of plane.axes) {
        singleArrowTempDir
          .copy(AXIS_UNIT_BY_INDEX[axisIndex])
          .applyQuaternion(quaternion);
        const towardPositive =
          singleArrowTempDir.dot(transformControls.eye) >= 0;
        const desiredSign = towardPositive ? 1 : -1;
        if (desiredSign === sign) continue;
        const visualMagnitude = Math.abs(
          plane.visual.scale.getComponent(axisIndex),
        );
        plane.visual.scale.setComponent(
          axisIndex,
          desiredSign * visualMagnitude,
        );
        const pickerMagnitude = Math.abs(
          plane.picker.scale.getComponent(axisIndex),
        );
        plane.picker.scale.setComponent(
          axisIndex,
          desiredSign * pickerMagnitude,
        );
        flipped = true;
      }
      if (flipped) {
        plane.visual.updateMatrixWorld(true);
        plane.picker.updateMatrixWorld(true);
      }
    }
  };

  // The library recomputes every handle's visible/scale/quaternion from
  // scratch inside `updateMatrixWorld` — called every render frame as part
  // of the normal scene traversal — so a one-time override would just get
  // overwritten on the next frame. Patching the instance (not the shared
  // prototype) re-applies ours immediately after, every time, for this
  // gizmo only.
  const originalUpdateMatrixWorld = gizmo.updateMatrixWorld.bind(gizmo);
  gizmo.updateMatrixWorld = (force) => {
    originalUpdateMatrixWorld(force);
    applyFacing();
  };
}

// Sandwiches GTAOPass in the composer chain: GTAOPass computes its own
// normal/depth G-buffer from the *live* camera (the same one editor helpers
// are visible to), and while three.js's own Line/Points auto-exclusion
// already keeps the grid/gizmo-icons/selection-outline (all LineSegments) out
// of that computation, the TransformControls gizmo's solid arrow/box handles
// are real Meshes and would otherwise fake-occlude the selected object. These
// two passes are pure side effects (no rendering, `needsSwap = false`) that
// hide every `isEditorHelper`-tagged object right before GTAOPass runs and
// restore them right after — RenderPass (the actual visible beauty image)
// has already captured them by then, so nothing disappears on screen.
class HelperVisibilityPass extends Pass {
  constructor(scene, mode, hiddenRef) {
    super();
    this.scene = scene;
    this.mode = mode;
    this.hiddenRef = hiddenRef;
    this.needsSwap = false;
  }
  render() {
    if (this.mode === "hide") {
      const hidden = [];
      this.scene.traverse((o) => {
        if (o.userData.isEditorHelper && o.visible) {
          o.visible = false;
          hidden.push(o);
        }
      });
      this.hiddenRef.current = hidden;
    } else {
      for (const o of this.hiddenRef.current) o.visible = true;
      this.hiddenRef.current = [];
    }
  }
}

// A WebGPU-capable canvas isn't required here: Three.js's WebGLRenderer is
// used instead of the newer, Chrome/Edge-only THREE.WebGPURenderer, so no
// navigator.gpu feature-detection/fallback is needed — WebGL2 is supported by
// every browser this app already targets.
const Viewport = forwardRef(function Viewport(
  {
    nodes,
    selectedNodeId,
    onSelectNode,
    gizmoMode,
    onGizmoModeChange,
    gizmoSpace,
    cameraMode,
    onTransformChange,
    backgroundColor,
    backgroundTextureUrl,
    ambientLightColor,
    ambientLightIntensity,
    worldDescription,
    worldReferenceImages,
    gridVisible,
    environmentEnabled,
    environmentIntensity,
    qualityPreset,
    renderMode,
    onObjectBoundsChange,
    onLightBoundsChange,
    // Named distinctly from environmentEnabled/environmentIntensity above
    // (the HDR reflection-environment toggle) — this is the scene.json
    // "environment" preview (ambient + per-light color/intensity/position),
    // an unrelated concept that just happens to share the word.
    onEnvironmentInfoChange,
    initialActiveCameraId,
    initialFreeCameraTransform,
    visibleProjections,
  },
  ref,
) {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const gridRef = useRef(null);
  const freeCameraRef = useRef(null);
  const renderCameraRef = useRef(null);
  const resizeRef = useRef(null);
  const transformControlsRef = useRef(null);
  const composerRef = useRef(null);
  const renderPassRef = useRef(null);
  const gtaoPassRef = useRef(null);
  const hideHelpersPassRef = useRef(null);
  const restoreHelpersPassRef = useRef(null);
  const depthHideHelpersPassRef = useRef(null);
  const depthRestoreHelpersPassRef = useRef(null);
  const depthMaterialRef = useRef(null);
  const environmentTextureRef = useRef(null);
  const qualityPresetRef = useRef(qualityPreset);
  qualityPresetRef.current = qualityPreset;
  const gizmoDraggingRef = useRef(false);
  const graphRootRef = useRef(null);
  const objectsByIdRef = useRef(new Map());
  const pickablesRef = useRef([]);
  const sceneHelpersRef = useRef([]);
  const selectionOutlineRef = useRef(null);
  const pointerDownRef = useRef(null);
  const lookDragRef = useRef(null); // {x, y, yaw, pitch} while a look-drag is in progress
  const orbitDragRef = useRef(null); // {x, y, radius, azimuth, polar} while an orbit-drag is in progress
  const orbitTargetRef = useRef(new THREE.Vector3(0, 0, 0)); // pivot orbit mode rotates the camera around
  // Unlocked (the default): the pivot recenters to a point in front of the
  // camera every time an orbit starts, so flying around with the arrow keys
  // never leaves it stranded far behind — the pivot follows the camera.
  // Locked (via Focus): the pivot stays pinned to the focused object's world
  // position instead, so flying around afterwards orbits *that* point — the
  // camera moves, the origin doesn't.
  const orbitLockedRef = useRef(false);
  const pressedKeysRef = useRef(new Set());
  const lastFrameTimeRef = useRef(null);

  const [activeCameraId, setActiveCameraId] = useState(null);
  const activeCameraIdRef = useRef(null);
  activeCameraIdRef.current = activeCameraId;
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
  // `resize` (below) lives inside the one-time renderer setup effect, so it
  // needs a ref (not the `nodes` prop directly) to read the piloted camera's
  // current aspect without that effect depending on — and tearing down the
  // renderer for — every node edit.
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;

  const onTransformChangeRef = useRef(onTransformChange);
  onTransformChangeRef.current = onTransformChange;
  const onGizmoModeChangeRef = useRef(onGizmoModeChange);
  onGizmoModeChangeRef.current = onGizmoModeChange;
  const onSelectNodeRef = useRef(onSelectNode);
  onSelectNodeRef.current = onSelectNode;
  const cameraModeRef = useRef(cameraMode);
  cameraModeRef.current = cameraMode;
  // Read inside the imperative animate() loop below (created once, on
  // mount), which can't see the reactive `renderMode` prop directly.
  const renderModeRef = useRef(renderMode);
  renderModeRef.current = renderMode;
  const aiObjectsOverlayRef = useRef(null);
  // The render box's current CSS-pixel size (set alongside `containerSize`
  // inside `resize()` below) — the AI Objects overlay canvas needs this
  // every frame, inside the same imperative loop, so it can't wait for the
  // (React-render-cycle-delayed) `containerSize` state to catch up.
  const renderBoxSizeRef = useRef({ w: 0, h: 0 });

  const cameraNodes = useMemo(
    () => nodes.filter((n) => n.type === "camera"),
    [nodes],
  );

  // Resolved once per actual visible-projection-set change (each layer's
  // frozen pose never needs per-frame updates — see projection.js) rather
  // than every render, so toggling/reordering doesn't re-fetch/re-decode the
  // same textures twice in a row and unrelated node edits don't rebuild this
  // for nothing. Safe to key off `visibleProjections` by reference — the
  // caller (Scene3DEditor.jsx) memoizes that array on its own `projections`
  // state, so it only gets a new identity on a genuine create/delete/toggle/
  // reorder.
  const projectionDescriptors = useMemo(
    () => resolveProjectionDescriptors(visibleProjections),
    [visibleProjections],
  );

  // The active navigation target: the free viewport camera, or — while
  // piloting a scene camera via the dropdown — that camera's own node group
  // (its child THREE.PerspectiveCamera has identity local transform, so
  // moving/rotating the group *is* moving/rotating the camera).
  const getActiveTarget = () => {
    const id = activeCameraIdRef.current;
    return (id && objectsByIdRef.current.get(id)) || freeCameraRef.current;
  };

  // Falls back to Free whenever the camera being piloted is deleted/no longer
  // exists (e.g. via the Delete key).
  useEffect(() => {
    if (activeCameraId && !cameraNodes.some((n) => n._id === activeCameraId)) {
      setActiveCameraId(null);
    }
  }, [activeCameraId, cameraNodes]);

  // Restores the piloted camera and the free camera's parked
  // position/rotation from a loaded scene. Scene3DEditor's fetch is async,
  // so these prop values are absent at mount and arrive a beat later (in the
  // same render as the loaded `nodes`, since both are set from the same
  // response) — applied once, here, then left alone: from that point on both
  // evolve as ordinary live Viewport state, exactly like a brand-new scene's
  // hardcoded starting camera would.
  useEffect(() => {
    if (!initialActiveCameraId) return;
    setActiveCameraId(initialActiveCameraId);
  }, [initialActiveCameraId]);

  useEffect(() => {
    const cam = freeCameraRef.current;
    const position = initialFreeCameraTransform?.position;
    if (!cam || !position) return;
    cam.position.set(position.x, position.y, position.z);
    const rotation = initialFreeCameraTransform?.rotation;
    if (rotation) {
      cam.rotation.set(
        rotation.x * DEG2RAD,
        rotation.y * DEG2RAD,
        rotation.z * DEG2RAD,
      );
    }
  }, [initialFreeCameraTransform]);

  // Commits whatever position/rotation the currently-piloted scene camera has
  // live (from keyboard flying or mouse-look) back into node state — called
  // on key/drag release, on switching away from that camera, on unmount, and
  // before a save so nothing is lost if the user saves mid-flight.
  const commitFlyTransform = () => {
    const id = activeCameraIdRef.current;
    const obj = id && objectsByIdRef.current.get(id);
    if (!obj) return;
    const { position, rotation } = extractTransform(obj);
    onTransformChangeRef.current?.(id, { position, rotation });
  };

  useImperativeHandle(ref, () => ({
    captureThumbnail: () =>
      new Promise((resolve) => {
        const renderer = rendererRef.current;
        const scene = sceneRef.current;
        const camera = renderCameraRef.current;
        if (!renderer || !scene || !camera) {
          resolve(null);
          return;
        }
        // Through the composer (not a direct renderer.render) so the saved
        // thumbnail matches what the editor actually shows — shadows, AO,
        // and tone mapping included.
        if (composerRef.current) composerRef.current.render();
        else renderer.render(scene, camera);
        renderer.domElement.toBlob((blob) => resolve(blob), "image/png");
      }),
    commitPendingTransform: () => commitFlyTransform(),
    // Which camera is currently piloted — null means Free Camera. Read
    // fresh at save time rather than mirrored into React state, since it
    // otherwise never needs to leave Viewport.
    getActiveCameraId: () => activeCameraIdRef.current,
    // Re-pilots a specific scene camera node from outside Viewport — used by
    // Scene3DEditor's "Project + move camera" (PropertiesPanel.jsx), which
    // just restored that node's transform/fov/aspect and needs the viewport
    // to actually look through it. Mirrors what the camera-picker <Select>
    // below does on its own onValueChange, EXCEPT the flush is skipped when
    // `cameraId` is already the piloted camera: commitFlyTransform reads
    // that camera's *current* live position and writes it back via
    // setNodes, which — if the caller just did its own setNodes to restore
    // that same node's transform (exactly what "Project + move camera" does)
    // — lands in the same batch and stomps the restoration right back to
    // wherever the camera happened to be a moment ago. Switching to a
    // genuinely different camera still flushes normally.
    pilotCamera: (cameraId) => {
      if ((cameraId ?? null) !== activeCameraIdRef.current) {
        commitFlyTransform();
      }
      setActiveCameraId(cameraId ?? null);
    },
    // The free-fly camera's live position/rotation — it's a plain
    // THREE.Object3D mutated directly during flight/orbit (never routed
    // through React state), so this always reflects wherever it was last
    // left, regardless of whether it's the camera currently being piloted.
    getFreeCameraTransform: () => {
      const cam = freeCameraRef.current;
      if (!cam) return null;
      const { position, rotation } = extractTransform(cam);
      return { position, rotation };
    },
    // Moves the orbit pivot onto the selected object and aims the active
    // camera at it in place (no repositioning) — "origin" here is that pivot,
    // not the camera's own position.
    focusOnSelected: () => {
      const obj = selectedNodeId && objectsByIdRef.current.get(selectedNodeId);
      const target = getActiveTarget();
      if (!obj || !target) return;
      obj.getWorldPosition(orbitTargetRef.current);
      orbitLockedRef.current = true;
      aimAt(target, orbitTargetRef.current);
      commitFlyTransform();
    },
    // Renders the beauty pass + scene.json for the AI generation pipeline,
    // off the live canvas (see renderPipeline.js) — the interactive viewport
    // itself is never resized or otherwise disturbed.
    renderAIPipeline: ({ width, height }) => {
      const renderer = rendererRef.current;
      const scene = sceneRef.current;
      const sourceCamera = renderCameraRef.current;
      if (!renderer || !scene || !sourceCamera) return Promise.resolve(null);
      return runAIRenderPipeline({
        renderer,
        scene,
        sourceCamera,
        nodes,
        sceneSettings: {
          aiDescription: worldDescription,
          aiReferenceImages: worldReferenceImages,
        },
        objectsById: objectsByIdRef.current,
        width,
        height,
        hasActiveProjection: visibleProjections?.length > 0,
        ambientLightColor,
        ambientLightIntensity,
      });
    },
  }));

  // One-time renderer/scene/camera/controls setup — independent of `nodes`,
  // which is handled by the effect below so editing the scene never tears
  // down and recreates the renderer.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const initialQuality = resolveQualityPreset(qualityPresetRef.current);

    // No `antialias: true` here — once rendering goes through EffectComposer
    // (below), the final blit to the canvas is a full-screen textured quad
    // via OutputPass, not rasterized geometry, so native MSAA never engages.
    // SMAAPass in the composer chain is what actually anti-aliases now.
    const renderer = new THREE.WebGLRenderer();
    renderer.setPixelRatio(
      Math.min(window.devicePixelRatio, initialQuality.pixelRatioCap),
    );
    renderer.shadowMap.enabled = true;
    // VSMShadowMap was tried here first for adjustable softness, but broke
    // shadows entirely — three.js's own WebGLShadowMap.js explicitly warns
    // "VSM shadow maps are not supported for PointLights". PCFSoftShadowMap
    // was tried next, but this three.js version has deprecated it and
    // silently aliases it straight to plain PCFShadowMap (see
    // WebGLShadowMap.js), which is why it looked hard-edged regardless.
    // PCFShadowMap, set explicitly, is what actually reads `shadow.radius`
    // for real multi-tap blur (confirmed in shadowmap_pars_fragment.glsl.js)
    // — see SHADOW_RADIUS/SHADOW_NORMAL_BIAS in sceneGraph.js.
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1;
    renderer.outputColorSpace = THREE.SRGBColorSpace; // already three's default — explicit for intent
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const scene = new THREE.Scene();
    // Real background color/texture is applied by the dedicated effects
    // below (keyed off the `backgroundColor`/`backgroundTextureUrl` props) —
    // this is just a same-tick placeholder so nothing ever reads an unset
    // background before those run.
    scene.background = new THREE.Color(0x1a1c20);
    // Fixed viewport decoration — not part of the saved node graph. Ambient
    // light is user-configurable (World settings) so it's built into the
    // rebuilt graph below instead of added here once.
    const grid = new THREE.GridHelper(10, 10, 0x3a3d44, 0x2a2c31);
    grid.userData.isEditorHelper = true;
    scene.add(grid);
    gridRef.current = grid;
    sceneRef.current = scene;

    // Procedural HDR/IBL environment (PMREM) — a neutral studio, not the
    // scene's actual background, so it lights/reflects PBR materials without
    // changing the editor's dark backdrop. `envScene` is a throwaway input to
    // `fromScene` (never added to the render loop), disposed right after.
    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    const envScene = new RoomEnvironment();
    const envRenderTarget = pmremGenerator.fromScene(envScene, 0.04);
    envScene.traverse((o) => {
      o.geometry?.dispose();
      o.material?.dispose();
    });
    environmentTextureRef.current = envRenderTarget;
    scene.environment =
      environmentEnabled !== false ? envRenderTarget.texture : null;
    scene.environmentIntensity = environmentIntensity ?? 1;

    const freeCamera = new THREE.PerspectiveCamera(50, 1, 0.1, 2000);
    freeCamera.position.set(6, 4, 8);
    freeCamera.lookAt(0, 0, 0); // one-time framing; navigation takes over from here, no continuous re-aim
    freeCamera.layers.enable(HELPER_LAYER); // see HELPER_LAYER's comment in sceneGraph.js
    freeCameraRef.current = freeCamera;
    renderCameraRef.current = freeCamera;

    const transformControls = new TransformControls(
      freeCamera,
      renderer.domElement,
    );
    transformControls.size = 0.6; // smaller/thinner handles — default 1 reads as oversized for this scene scale
    transformControls.addEventListener("dragging-changed", (event) => {
      gizmoDraggingRef.current = event.value;
      if (!event.value) {
        const obj = transformControls.object;
        if (obj?.userData?.nodeId) {
          onTransformChangeRef.current?.(
            obj.userData.nodeId,
            extractTransform(obj),
          );
        }
      }
    });
    const transformControlsHelper = transformControls.getHelper();
    // Hidden (via `.visible`, not HELPER_LAYER) for AI render exports —
    // TransformControls keeps its own internal Raycaster for hit-testing drag
    // handles, defaulted to layer 0 only, so moving its picker meshes to a
    // different layer would silently break gizmo dragging. Simple visibility
    // toggling is safe here because it only ever happens while the export
    // modal (a blocking overlay) is open, so there's no interaction to break.
    transformControlsHelper.userData.isEditorHelper = true;
    scene.add(transformControlsHelper);
    transformControlsRef.current = transformControls;
    installSingleArrowGizmo(transformControls);

    // Post-processing chain: hide helpers (Depth Map only) → RenderPass (the
    // actual beauty image, with helpers visible in Shaded) → restore helpers
    // (Depth Map only) → hide helpers → GTAOPass (ambient occlusion, tuned
    // tight enough to double as a contact-shadow proxy) → restore helpers →
    // SMAAPass (anti-aliasing) → OutputPass (applies tone mapping + output
    // color space exactly once, at the very end — see HelperVisibilityPass's
    // own comment for why the hide/restore pair is needed). Sized with
    // whatever the container measures right now; `resize()` below corrects
    // it immediately, before the first frame ever paints.
    const composer = new EffectComposer(renderer);
    composerRef.current = composer;

    // Depth Map wraps RenderPass itself (not just GTAO's own normal/depth
    // prepass below): swapping the whole scene to a depth material still
    // renders every isEditorHelper mesh (light icons, camera gizmos, the
    // grid, the TransformControls arrows) as its own bogus "depth", so they
    // need to be gone for the one pass that actually produces the visible
    // image. Disabled (no-op) outside Depth Map — see the renderMode effect.
    const depthHiddenRef = { current: [] };
    const hideHelpersForDepthPass = new HelperVisibilityPass(
      scene,
      "hide",
      depthHiddenRef,
    );
    hideHelpersForDepthPass.enabled = false;
    depthHideHelpersPassRef.current = hideHelpersForDepthPass;
    composer.addPass(hideHelpersForDepthPass);

    const renderPass = new RenderPass(scene, freeCamera);
    renderPassRef.current = renderPass;
    composer.addPass(renderPass);

    const restoreHelpersForDepthPass = new HelperVisibilityPass(
      scene,
      "restore",
      depthHiddenRef,
    );
    restoreHelpersForDepthPass.enabled = false;
    depthRestoreHelpersPassRef.current = restoreHelpersForDepthPass;
    composer.addPass(restoreHelpersForDepthPass);

    const hiddenHelpersRef = { current: [] };
    const hideHelpersPass = new HelperVisibilityPass(
      scene,
      "hide",
      hiddenHelpersRef,
    );
    hideHelpersPassRef.current = hideHelpersPass;
    composer.addPass(hideHelpersPass);

    const gtaoPass = new GTAOPass(
      scene,
      freeCamera,
      container.clientWidth || 1,
      container.clientHeight || 1,
      {},
      {
        // World-space radius (screenSpaceRadius left at its default false) —
        // the screen-space mode rescales this by view depth internally
        // (see GTAOShader.js), which made the effect nearly imperceptible at
        // this value; plain world units are predictable at this scene's
        // scale (default cube is 1 unit, plane is 10).
        radius: 0.5,
        thickness: 1,
        distanceExponent: 1,
        scale: 1,
        samples: initialQuality.ao.samples ?? 16,
      },
    );
    gtaoPass.output = GTAOPass.OUTPUT.Default;
    gtaoPass.enabled = initialQuality.ao.enabled;
    gtaoPassRef.current = gtaoPass;
    composer.addPass(gtaoPass);

    const restoreHelpersPass = new HelperVisibilityPass(
      scene,
      "restore",
      hiddenHelpersRef,
    );
    restoreHelpersPassRef.current = restoreHelpersPass;
    composer.addPass(restoreHelpersPass);

    hideHelpersPass.enabled = initialQuality.ao.enabled;
    restoreHelpersPass.enabled = initialQuality.ao.enabled;

    const smaaPass = new SMAAPass();
    composer.addPass(smaaPass);

    const outputPass = new OutputPass();
    composer.addPass(outputPass);

    // Swapped in as `scene.overrideMaterial` for the Depth Map render pass
    // (see the renderMode effect below). A hand-rolled shader rather than
    // THREE.MeshDepthMaterial: that material packs the camera's own
    // non-linear NDC depth (tied to its near/far clip planes), but the
    // user-facing `zDepthDistance` on a camera node is a plain linear
    // distance in front of the camera at which the pass should hit 0/black,
    // unrelated to clipping. `uZDepthDistance` is updated live (no shader
    // recompile) by the activeCameraId effect below, off whichever camera
    // node is actually piloting the viewport. No skinning/morph support —
    // sceneGraph.js never builds a THREE.SkinnedMesh, so plain positions are
    // enough. DoubleSide to match sceneGraph.js's own mesh materials (every
    // MaterialClass there is built with `side: THREE.DoubleSide`) —
    // overrideMaterial replaces the per-object material wholesale, so
    // without this a plane would render backface-culled here despite being
    // visible from both sides in Shaded.
    const depthMaterial = new THREE.ShaderMaterial({
      side: THREE.DoubleSide,
      uniforms: {
        uZDepthDistance: { value: DEFAULT_Z_DEPTH_DISTANCE },
      },
      vertexShader: `
        varying float vViewDistance;
        void main() {
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          // Distance along the camera's own forward axis (view-space -Z),
          // not radial distance to the camera's origin — the usual z-depth
          // convention.
          vViewDistance = -mvPosition.z;
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform float uZDepthDistance;
        varying float vViewDistance;
        void main() {
          float value = clamp(
            1.0 - vViewDistance / max(uZDepthDistance, 0.0001),
            0.0,
            1.0
          );
          gl_FragColor = vec4(vec3(value), 1.0);
        }
      `,
    });
    depthMaterialRef.current = depthMaterial;

    const resize = () => {
      const { clientWidth: cw, clientHeight: ch } = container;
      if (!cw || !ch) return;

      let w, h;
      if (activeCameraIdRef.current) {
        // Piloting: contain-fit the camera's own configured aspect within
        // the actual panel size, so e.g. a 9:16 camera fills the panel's
        // height (pillarboxed) instead of being squeezed into a fixed 16:9
        // box — the piloted camera's own `.aspect` is set to match this same
        // value in the camera-switch effect below.
        const pilotedNode = nodesRef.current.find(
          (n) => n._id === activeCameraIdRef.current,
        );
        const camAspect = pilotedNode?.aspect || DEFAULT_CAMERA_ASPECT;
        const containerAspect = cw / ch;
        w = containerAspect > camAspect ? ch * camAspect : cw;
        h = containerAspect > camAspect ? ch : cw / camAspect;
      } else {
        // Free camera: fill whatever space is available, as before.
        w = cw;
        h = ch;
        freeCameraRef.current.aspect = cw / ch;
        freeCameraRef.current.updateProjectionMatrix();
      }
      renderer.setSize(w, h);
      composerRef.current?.setSize(w, h);
      const aoScale =
        resolveQualityPreset(qualityPresetRef.current).ao.resolutionScale ?? 1;
      gtaoPassRef.current?.setSize(
        Math.max(1, Math.round(w * aoScale)),
        Math.max(1, Math.round(h * aoScale)),
      );
      setContainerSize({ w, h });
      renderBoxSizeRef.current = { w, h };
    };
    resizeRef.current = resize;
    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);

    const forward = new THREE.Vector3();
    const right = new THREE.Vector3();
    const worldQuat = new THREE.Quaternion();
    const delta = new THREE.Vector3();

    // Forward/right derived from the target's actual world orientation (via
    // its local -Z / +X axes, the standard camera-forward convention) rather
    // than a cross-product against world-up — that alternative reads as
    // "forward" but is actually the object's local +Z (i.e. backward for
    // anything using the look-down/-Z convention, cameras included).
    const getForwardRight = (target) => {
      target.getWorldQuaternion(worldQuat);
      forward.set(0, 0, -1).applyQuaternion(worldQuat);
      right.set(1, 0, 0).applyQuaternion(worldQuat);
    };

    // Arrow keys fly the active target (see getActiveTarget). Held keys move
    // every frame rather than once per keydown, for smooth continuous flight.
    const applyFlyMovement = (dt) => {
      const keys = pressedKeysRef.current;
      if (keys.size === 0) return;
      const target = getActiveTarget();
      if (!target) return;

      target.updateWorldMatrix(true, false);
      getForwardRight(target);

      const speed = (keys.has("Shift") ? FLY_SPEED * 2 : FLY_SPEED) * dt;
      delta.set(0, 0, 0);
      if (keys.has("ArrowUp") || keys.has("w"))
        delta.addScaledVector(forward, speed);
      if (keys.has("ArrowDown") || keys.has("s"))
        delta.addScaledVector(forward, -speed);
      if (keys.has("ArrowRight") || keys.has("d"))
        delta.addScaledVector(right, speed);
      if (keys.has("ArrowLeft") || keys.has("a"))
        delta.addScaledVector(right, -speed);
      // Straight world-up/-down regardless of pitch — tying this to the
      // target's own local up instead would tilt "up" sideways the moment
      // the camera pitches, which reads as broken rather than useful.
      if (keys.has("q")) delta.y += speed;
      if (keys.has("e")) delta.y -= speed;
      if (delta.lengthSq() === 0) return;

      target.position.add(delta);
      // Flying away invalidates a locked focus target (see focusOnSelected) —
      // without this, orbiting after flying kept re-centering on wherever was
      // last focused (e.g. back near the world origin) instead of following
      // the camera to its new position, silently ignoring "unlocked" being
      // the whole point of moving away in the first place.
      orbitLockedRef.current = false;
    };

    const strokeBounds = (ctx, boundsList, w, h) => {
      for (const b of boundsList) {
        if (!b) continue;
        const { x, y, width, height } = b.boundingBox;
        ctx.strokeStyle = b.color;
        ctx.strokeRect(x * w, y * h, width * w, height * h);
      }
    };

    // AI Objects is an overlay on top of Shaded (unlike Depth Map, which
    // swaps `scene.overrideMaterial`) — drawn on a separate 2D canvas laid
    // exactly over the WebGL one (see the JSX below) rather than into the
    // composer chain, since it's screen-space annotations, not a rendered
    // material. Always clears first (even outside "aiObjects") so switching
    // away never leaves stale boxes on screen; the heavier group/projection
    // work below that is skipped otherwise. Recomputes semantic groups/
    // lights from scratch every frame rather than caching — this editor's
    // scenes are small enough that the cost is negligible, and it keeps the
    // boxes trivially always in sync with the latest nodes/camera with no
    // invalidation logic to get wrong.
    const drawAiObjectsOverlay = () => {
      const canvas = aiObjectsOverlayRef.current;
      if (!canvas) return;
      const { w, h } = renderBoxSizeRef.current;
      if (canvas.width !== w) canvas.width = w;
      if (canvas.height !== h) canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (renderModeRef.current !== "aiObjects" || !w || !h) return;

      const camera = renderCameraRef.current;
      if (!camera) return;
      const groups = buildSemanticGroups(nodesRef.current);
      const lights = buildSemanticLights(nodesRef.current);
      if (!groups.length && !lights.length) return;

      ctx.lineWidth = 2;
      if (groups.length) {
        strokeBounds(
          ctx,
          computeObjectBounds({
            groups,
            objectsById: objectsByIdRef.current,
            camera,
          }),
          w,
          h,
        );
      }
      if (lights.length) {
        strokeBounds(
          ctx,
          computeLightBounds({
            lights,
            objectsById: objectsByIdRef.current,
            camera,
          }),
          w,
          h,
        );
      }
    };

    let frameId;
    const animate = (time) => {
      const last = lastFrameTimeRef.current ?? time;
      const dt = Math.min((time - last) / 1000, 0.1);
      lastFrameTimeRef.current = time;

      applyFlyMovement(dt);
      // CameraHelper/PointLightHelper reposition automatically with their
      // tracked camera/light (via the matrixWorld-reference trick — see
      // sceneGraph.js), but re-deriving their own shape (CameraHelper's
      // frustum) only happens when .update() runs — called every frame, not
      // just once per rebuild, so a live gizmo drag (which changes the
      // tracked object continuously, with no rebuild until it commits at
      // release) can't leave them stale.
      for (const helper of sceneHelpersRef.current) helper.update();
      composer.render();
      drawAiObjectsOverlay();
      frameId = requestAnimationFrame(animate);
    };
    frameId = requestAnimationFrame(animate);

    const raycaster = new THREE.Raycaster();
    raycaster.params.Line.threshold = 0.1; // thin helper lines (axes/frustum) are otherwise hard to click
    raycaster.layers.enable(HELPER_LAYER); // so null nodes' AxesHelper (HELPER_LAYER-only) stay clickable
    const toNdc = (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      return new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
      );
    };
    // While piloting a camera, its own gizmo (a small frustum icon sitting
    // right at that camera's origin, fanning out along its own view axis —
    // see createCameraGizmo) is otherwise the closest possible hit for
    // almost *any* ray cast from that same origin, since the ray and the
    // gizmo's lines share a start point. Left in, clicking anywhere while
    // piloting a camera just kept re-selecting that camera and nothing else
    // was ever reachable.
    const getPickables = () => {
      const pilotedId = activeCameraIdRef.current;
      return pilotedId
        ? pickablesRef.current.filter(
            (obj) => obj.userData.nodeId !== pilotedId,
          )
        : pickablesRef.current;
    };

    const startEuler = new THREE.Euler(0, 0, 0, "YXZ");
    const lookEuler = new THREE.Euler(0, 0, 0, "YXZ");
    const orbitOffset = new THREE.Vector3();

    const handlePointerDown = (event) => {
      if (event.button !== 0) return;
      // A gizmo-handle drag is starting — don't let click-to-select or
      // mouse-look below treat this interaction as theirs.
      if (transformControls.axis != null) {
        pointerDownRef.current = null;
        return;
      }
      pointerDownRef.current = { x: event.clientX, y: event.clientY };
      renderer.domElement.setPointerCapture(event.pointerId);

      // Camera orbit/look always arms here too, even when the press lands on
      // an object — only an actual gizmo-handle grab (checked above) opts
      // out. A plain click still selects: handlePointerUp only treats this as
      // a look-around drag once the pointer has moved past
      // CLICK_DRAG_THRESHOLD_PX, so a real click never rotates the camera by
      // more than that sub-pixel-scale, imperceptible amount.
      const target = getActiveTarget();
      if (cameraModeRef.current === "orbit") {
        if (!orbitLockedRef.current) {
          getForwardRight(target);
          orbitTargetRef.current
            .copy(target.position)
            .addScaledVector(forward, DEFAULT_ORBIT_DISTANCE);
        }
        orbitOffset.copy(target.position).sub(orbitTargetRef.current);
        const radius = Math.max(orbitOffset.length(), 0.001);
        orbitDragRef.current = {
          x: event.clientX,
          y: event.clientY,
          radius,
          azimuth: Math.atan2(orbitOffset.x, orbitOffset.z),
          polar: Math.acos(
            THREE.MathUtils.clamp(orbitOffset.y / radius, -1, 1),
          ),
        };
      } else {
        target.getWorldQuaternion(worldQuat);
        startEuler.setFromQuaternion(worldQuat, "YXZ");
        lookDragRef.current = {
          x: event.clientX,
          y: event.clientY,
          yaw: startEuler.y,
          pitch: startEuler.x,
        };
      }
    };
    const handlePointerMove = (event) => {
      if (gizmoDraggingRef.current) return;
      const target = getActiveTarget();
      if (!target) return;

      const orbit = orbitDragRef.current;
      if (orbit) {
        const azimuth =
          orbit.azimuth - (event.clientX - orbit.x) * ORBIT_SENSITIVITY;
        const polar = THREE.MathUtils.clamp(
          orbit.polar - (event.clientY - orbit.y) * ORBIT_SENSITIVITY,
          ORBIT_POLAR_LIMIT,
          Math.PI - ORBIT_POLAR_LIMIT,
        );
        const sinPolar = Math.sin(polar);
        target.position.set(
          orbitTargetRef.current.x +
            orbit.radius * sinPolar * Math.sin(azimuth),
          orbitTargetRef.current.y + orbit.radius * Math.cos(polar),
          orbitTargetRef.current.z +
            orbit.radius * sinPolar * Math.cos(azimuth),
        );
        aimAt(target, orbitTargetRef.current);
        return;
      }

      const drag = lookDragRef.current;
      if (!drag) return;
      const yaw = drag.yaw - (event.clientX - drag.x) * LOOK_SENSITIVITY;
      const pitch = Math.max(
        -PITCH_LIMIT,
        Math.min(
          PITCH_LIMIT,
          drag.pitch - (event.clientY - drag.y) * LOOK_SENSITIVITY,
        ),
      );
      lookEuler.set(pitch, yaw, 0);
      target.quaternion.setFromEuler(lookEuler);
    };
    const handlePointerUp = (event) => {
      const wasNavigating = !!lookDragRef.current || !!orbitDragRef.current;
      lookDragRef.current = null;
      orbitDragRef.current = null;
      if (wasNavigating) commitFlyTransform();

      const start = pointerDownRef.current;
      pointerDownRef.current = null;
      if (!start) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (Math.hypot(dx, dy) > CLICK_DRAG_THRESHOLD_PX) return; // was a look-around drag

      raycaster.setFromCamera(toNdc(event), renderCameraRef.current);
      const hits = raycaster.intersectObjects(getPickables(), true);
      onSelectNodeRef.current?.(
        hits.length ? hits[0].object.userData.nodeId : null,
      );
    };
    const handleWheel = (event) => {
      event.preventDefault();
      const target = getActiveTarget();
      if (!target) return;
      getForwardRight(target);
      target.position.addScaledVector(forward, -event.deltaY * WHEEL_SPEED);
      orbitLockedRef.current = false; // see the note in applyFlyMovement
      if (activeCameraIdRef.current) commitFlyTransform();
    };

    const handleKeyDown = (event) => {
      if (isTypingTarget(document.activeElement)) return;
      // A held Cmd/Ctrl turns this into an OS/browser chord (Cmd+S save,
      // Ctrl+Z undo, ...), not a single-letter hotkey — WASD/QE movement and
      // the 1-4 gizmo-mode keys only fire on a bare keypress.
      if (event.metaKey || event.ctrlKey) return;
      if (event.key === "Shift") {
        pressedKeysRef.current.add("Shift");
        setGizmoSnap(transformControls, true);
      }
      const key = normalizeKey(event.key);
      if (MOVEMENT_KEYS.includes(key)) {
        event.preventDefault();
        pressedKeysRef.current.add(key);
      }
      const mode = GIZMO_KEY_TO_MODE[key];
      if (mode) onGizmoModeChangeRef.current?.(mode);
    };
    const handleKeyUp = (event) => {
      if (event.key === "Shift") {
        pressedKeysRef.current.delete("Shift");
        setGizmoSnap(transformControls, false);
      }
      const key = normalizeKey(event.key);
      if (MOVEMENT_KEYS.includes(key)) {
        pressedKeysRef.current.delete(key);
        commitFlyTransform();
      }
    };
    const handleBlur = () => {
      pressedKeysRef.current.clear();
      setGizmoSnap(transformControls, false);
    };

    const dom = renderer.domElement;
    dom.addEventListener("pointerdown", handlePointerDown);
    dom.addEventListener("pointermove", handlePointerMove);
    dom.addEventListener("pointerup", handlePointerUp);
    dom.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      dom.removeEventListener("pointerdown", handlePointerDown);
      dom.removeEventListener("pointermove", handlePointerMove);
      dom.removeEventListener("pointerup", handlePointerUp);
      dom.removeEventListener("wheel", handleWheel);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
      commitFlyTransform();
      transformControls.dispose();
      composer.dispose();
      gtaoPass.dispose();
      smaaPass.dispose();
      outputPass.dispose();
      depthMaterial.dispose();
      pmremGenerator.dispose();
      envRenderTarget.dispose();
      if (graphRootRef.current) disposeSceneGraph(graphRootRef.current);
      if (selectionOutlineRef.current) {
        selectionOutlineRef.current.geometry.dispose();
        selectionOutlineRef.current.material.dispose();
      }
      renderer.dispose();
      container.removeChild(renderer.domElement);
    };
    // Intentionally empty — this effect owns setup/teardown of the renderer
    // itself, not the per-edit scene graph (handled below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stays referentially identical to its own last value whenever `nodes`
  // only changed in ways from NODE_FIELDS_IRRELEVANT_TO_REBUILD above — the
  // rebuild effect below is keyed on this instead of the raw `nodes` prop, so
  // e.g. typing into a node's AI Description textarea (which calls setNodes
  // on every keystroke) no longer re-triggers the full scene-graph teardown/
  // rebuild below. Every *other* effect in this file still reads the raw
  // `nodes` prop directly — anything that's actually supposed to track an
  // edit live (the AI Objects overlay, etc.) is unaffected.
  const rebuildComparableRef = useRef(null);
  const rebuildNodesRef = useRef(nodes);
  const rebuildComparable = useMemo(
    () => nodes.map((n) => omit(n, NODE_FIELDS_IRRELEVANT_TO_REBUILD)),
    [nodes],
  );
  if (!isEqual(rebuildComparable, rebuildComparableRef.current)) {
    rebuildComparableRef.current = rebuildComparable;
    rebuildNodesRef.current = nodes;
  }
  const rebuildNodes = rebuildNodesRef.current;

  // Rebuilds the Three.js scene graph from `rebuildNodes` (plus the World
  // ambient light settings) — the expensive step (disposes and recreates
  // every geometry/material/mesh in the scene). Selection/gizmo-mode changing
  // on their own (every click, every W/E/R/Q press) used to be in this same
  // dependency list, which meant merely clicking an object or switching tools
  // tore down and rebuilt the *entire* scene — visibly flickering — for a
  // change that never touched the actual scene data. That's now handled by
  // the cheap selection-only effect below instead.
  //
  // Deliberately synchronous, not deferred/throttled — the selection-outline
  // effect right below it (and the camera-switch effect further down) both
  // depend on `nodes` too and read objectsByIdRef.current expecting this
  // effect to have already repopulated it with this same render's objects;
  // deferring this one a frame would leave them re-attaching to the old,
  // about-to-be-disposed graph with nothing to re-run them once the real
  // rebuild lands. Burst-of-ticks coalescing instead happens one level up, at
  // the `nodes`/sceneSettings state updates themselves (Scene3DEditor.jsx) —
  // so by the time `nodes` actually changes here, every effect in this file
  // that depends on it changes together, in the same commit, same as before.
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    if (graphRootRef.current) {
      scene.remove(graphRootRef.current);
      disposeSceneGraph(graphRootRef.current);
    }

    // Releases GPU depth-target memory for any projection that's no longer
    // visible/no longer exists — projection.js's depthTargets Map is
    // module-level (survives across rebuilds, unlike buildSceneGraph's own
    // backdrop Map), so without this, repeatedly toggling projections on/off
    // over a session would leak one WebGLRenderTarget + DepthTexture per
    // hidden projection forever.
    pruneProjectorDepthTargets(new Set(projectionDescriptors.map((d) => d.id)));

    // Refreshes each visible projection's own occlusion depth map against
    // whatever geometry actually exists right now — every projected
    // material's shader already samples these same (stable) depth texture
    // references (see buildSceneGraph's `projectionDescriptors` option /
    // applyProjectionsToMaterial), so this just needs to keep their contents
    // current. No-ops when nothing is visible. Called both synchronously
    // below (right after the rebuild) and again via `onCustomModelLoaded` —
    // a "custom" (GLB) node's actual mesh isn't attached until its async load
    // resolves, well after this synchronous rebuild returns, so without the
    // second call the depth maps would permanently miss that model's
    // contribution (letting projections show through it as if nothing were
    // there).
    const refreshProjectorDepth = () => {
      for (const projection of visibleProjections) {
        renderProjectorDepth({
          renderer: rendererRef.current,
          scene,
          projection,
        });
      }
    };

    const { root, objectsById, pickables, sceneHelpers } = buildSceneGraph(
      rebuildNodes,
      {
        ambientLight: {
          color: ambientLightColor,
          intensity: ambientLightIntensity,
        },
        projectionDescriptors,
        projectionPoses: visibleProjections,
        onCustomModelLoaded: refreshProjectorDepth,
      },
    );
    scene.add(root);
    graphRootRef.current = root;
    objectsByIdRef.current = objectsById;
    pickablesRef.current = pickables;
    sceneHelpersRef.current = sceneHelpers;

    refreshProjectorDepth();

    // Every rebuild discards the *previous* camera object(s) (this function
    // always builds brand-new THREE.Object3Ds — see buildSceneGraph's own
    // comment) — without this, renderCameraRef/renderPass/gtaoPass/
    // transformControls would keep pointing at whatever camera object
    // existed before this rebuild until the separate "camera switch" effect
    // below happens to also re-run (it only depends on activeCameraId/nodes/
    // cameraNodes, so a rebuild triggered by something else entirely — e.g.
    // toggling a projection on/off, which only touches sceneSettings, or an
    // ambient-light change — leaves it stale). That gap is exactly what made
    // piloting a camera look frozen (gizmo/orbit target moving while the
    // rendered view didn't) right after such a rebuild, until some *later*,
    // unrelated nodes change finally caused the camera-switch effect to
    // catch up and "snap" the view to where it should already have been.
    const pilotedId = activeCameraIdRef.current;
    const pilotedCamera = pilotedId
      ? objectsById.get(pilotedId)?.userData.camera
      : null;
    const freshRenderCamera = pilotedCamera || freeCameraRef.current;
    renderCameraRef.current = freshRenderCamera;
    if (renderPassRef.current) renderPassRef.current.camera = freshRenderCamera;
    if (gtaoPassRef.current) gtaoPassRef.current.camera = freshRenderCamera;
    if (transformControlsRef.current) {
      transformControlsRef.current.camera = freshRenderCamera;
    }
  }, [
    rebuildNodes,
    ambientLightColor,
    ambientLightIntensity,
    projectionDescriptors,
    visibleProjections,
  ]);

  // Selection outline + gizmo attach/detach — deliberately cheap and
  // separate from the rebuild above: just swaps a small BoxHelper and calls
  // TransformControls.attach()/detach() on the *existing* scene graph,
  // touching nothing else. Still re-runs after `nodes` changes too, since a
  // rebuild replaces `objectsByIdRef.current` with fresh objects that need
  // re-attaching, but it doesn't perform or wait on that rebuild itself.
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    if (selectionOutlineRef.current) {
      scene.remove(selectionOutlineRef.current);
      selectionOutlineRef.current.geometry.dispose();
      selectionOutlineRef.current.material.dispose();
      selectionOutlineRef.current = null;
    }

    const selectedObject = selectedNodeId
      ? objectsByIdRef.current.get(selectedNodeId)
      : null;
    const selectedNode = selectedNodeId
      ? nodes.find((n) => n._id === selectedNodeId)
      : null;
    if (selectedObject && selectedNode?.type === "mesh") {
      const outline = new THREE.BoxHelper(selectedObject, 0xffcc00);
      outline.userData.isEditorHelper = true;
      scene.add(outline);
      selectionOutlineRef.current = outline;
    }

    // "select" is a pure-selection mode — no manipulation handles, so the
    // gizmo stays detached even with something selected.
    const gizmoTarget =
      selectedObject && gizmoMode !== "select" ? selectedObject : null;
    if (gizmoTarget) transformControlsRef.current?.attach(gizmoTarget);
    else transformControlsRef.current?.detach();
  }, [nodes, selectedNodeId, gizmoMode]);

  useEffect(() => {
    if (transformControlsRef.current && gizmoMode !== "select") {
      transformControlsRef.current.mode = gizmoMode || "translate";
    }
  }, [gizmoMode]);

  useEffect(() => {
    if (gridRef.current) gridRef.current.visible = gridVisible !== false;
  }, [gridVisible]);

  // Toggles/rescales the already-generated PMREM environment — doesn't
  // regenerate it, so this is cheap regardless of how often the World
  // settings sliders change.
  useEffect(() => {
    const scene = sceneRef.current;
    const envRenderTarget = environmentTextureRef.current;
    if (!scene || !envRenderTarget) return;
    scene.environment =
      environmentEnabled !== false ? envRenderTarget.texture : null;
    scene.environmentIntensity = environmentIntensity ?? 1;
  }, [environmentEnabled, environmentIntensity]);

  // Solid-color background — skipped while a background texture is set,
  // since THREE.Scene.background can only ever be one or the other; the
  // texture effect below owns restoring it once the texture is cleared.
  // Also skipped (forced to black instead) during the Depth Map pass: empty
  // space is further from the camera than anything the scene could put in
  // front of it, so it always belongs at that pass's black/"0" end,
  // regardless of whatever World settings has configured for Shaded —
  // `renderMode` is a dependency here so leaving Depth Map immediately
  // restores whichever of these two this scene actually uses.
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene || backgroundTextureUrl) return;
    scene.background =
      renderMode === "depth"
        ? DEPTH_MAP_BACKGROUND_COLOR
        : new THREE.Color(backgroundColor || "#1a1c20");
  }, [backgroundColor, backgroundTextureUrl, renderMode]);

  // Background texture — loaded fresh on every URL change and disposed on
  // cleanup (unmount, URL change, or the texture being cleared), same
  // teardown discipline as the PMREM environment render target above.
  // Short-circuits to black instead of loading anything while in Depth Map,
  // for the same "background is always Depth Map's black" reason as the
  // solid-color effect above — re-runs (without a real reload, since this
  // branch returns before ever touching the network) the moment `renderMode`
  // flips back, restoring the texture.
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene || !backgroundTextureUrl) return;
    if (renderMode === "depth") {
      scene.background = DEPTH_MAP_BACKGROUND_COLOR;
      return;
    }
    const texture = new THREE.TextureLoader().load(backgroundTextureUrl);
    texture.colorSpace = THREE.SRGBColorSpace;
    scene.background = texture;
    return () => texture.dispose();
  }, [backgroundTextureUrl, renderMode]);

  // Quality preset drives render cost, not scene data: pixel ratio cap
  // (renderer + composer, which track it separately) and AO sample
  // count/on-off. Re-runs `resize()` immediately after so the AO render
  // target's resolution-scaled size and the renderer/composer's pixel ratio
  // take effect right away rather than waiting for the next real resize.
  // Also owns the Depth Map render pass: AO and ACES tone mapping both
  // operate on lit color, which the color buffer no longer holds once
  // `scene.overrideMaterial` is swapped to raw depth, so both are forced off
  // for as long as `renderMode` is "depth" regardless of the quality preset.
  // `outputColorSpace` is forced to linear (no sRGB encode) for the same
  // reason: OutputPass otherwise runs every pixel through the sRGB transfer
  // curve before it reaches the screen, and that curve is steep near 0 —
  // most of a *linear* distance ramp's visible range gets compressed into a
  // thin sliver right at the black end, reading as an abrupt cut to black
  // rather than the smooth, uniform fade the shader actually computes.
  // Depth Map is also look-only: it hides every editor helper (light icons,
  // camera gizmos, the grid, the TransformControls arrows) from the render
  // and disables TransformControls interaction outright, since a hidden
  // handle would otherwise still be draggable.
  useEffect(() => {
    const renderer = rendererRef.current;
    const scene = sceneRef.current;
    const composer = composerRef.current;
    const gtaoPass = gtaoPassRef.current;
    const hideHelpersPass = hideHelpersPassRef.current;
    const restoreHelpersPass = restoreHelpersPassRef.current;
    const depthHideHelpersPass = depthHideHelpersPassRef.current;
    const depthRestoreHelpersPass = depthRestoreHelpersPassRef.current;
    const transformControls = transformControlsRef.current;
    if (!renderer || !scene || !composer || !gtaoPass) return;

    const preset = resolveQualityPreset(qualityPreset);
    const pixelRatio = Math.min(window.devicePixelRatio, preset.pixelRatioCap);
    renderer.setPixelRatio(pixelRatio);
    composer.setPixelRatio(pixelRatio);

    const isDepthPass = renderMode === "depth";
    scene.overrideMaterial = isDepthPass ? depthMaterialRef.current : null;
    renderer.toneMapping = isDepthPass
      ? THREE.NoToneMapping
      : THREE.ACESFilmicToneMapping;
    renderer.outputColorSpace = isDepthPass
      ? THREE.LinearSRGBColorSpace
      : THREE.SRGBColorSpace;
    if (depthHideHelpersPass) depthHideHelpersPass.enabled = isDepthPass;
    if (depthRestoreHelpersPass) depthRestoreHelpersPass.enabled = isDepthPass;
    if (transformControls) transformControls.enabled = !isDepthPass;

    const aoEnabled = preset.ao.enabled && !isDepthPass;
    gtaoPass.enabled = aoEnabled;
    if (hideHelpersPass) hideHelpersPass.enabled = aoEnabled;
    if (restoreHelpersPass) restoreHelpersPass.enabled = aoEnabled;
    if (aoEnabled) {
      gtaoPass.updateGtaoMaterial({ samples: preset.ao.samples });
    }

    resizeRef.current?.();
  }, [qualityPreset, renderMode]);

  // Scale is always applied in local space regardless of this setting — a
  // three.js TransformControls limitation (non-uniform scale has no
  // consistent meaning in world space), not something toggleable here.
  useEffect(() => {
    if (transformControlsRef.current)
      transformControlsRef.current.space = gizmoSpace || "world";
  }, [gizmoSpace]);

  // Switches which camera the renderer/gizmo use: the free viewport camera,
  // or — when piloting one via the dropdown — that scene camera's own
  // THREE.PerspectiveCamera (a child of its node's transform group, so its
  // world matrix already reflects the node's position/rotation).
  useEffect(() => {
    const container = containerRef.current;
    const freeCamera = freeCameraRef.current;
    const transformControls = transformControlsRef.current;
    if (!container || !freeCamera || !transformControls) return;

    const pilotedNode = activeCameraId
      ? nodes.find((n) => n._id === activeCameraId)
      : null;
    const piloted = activeCameraId
      ? objectsByIdRef.current.get(activeCameraId)?.userData.camera
      : null;
    const renderCamera = piloted || freeCamera;
    renderCameraRef.current = renderCamera;
    if (renderPassRef.current) renderPassRef.current.camera = renderCamera;
    if (gtaoPassRef.current) gtaoPassRef.current.camera = renderCamera;
    // The Free Camera has no backing node to read a configured distance off
    // of, same situation as its fov/aspect having no node-level source —
    // falls back to the same default a brand-new camera node starts with.
    if (depthMaterialRef.current) {
      depthMaterialRef.current.uniforms.uZDepthDistance.value =
        pilotedNode?.zDepthDistance ?? DEFAULT_Z_DEPTH_DISTANCE;
    }

    if (piloted) {
      // The render box itself (see `resize` below) is sized to contain-fit
      // this same aspect within the panel, so the camera's own configured
      // aspect is exactly what should end up on screen — a portrait camera
      // fills the panel's height instead of being pillarboxed inside a
      // fixed-16:9 render box.
      piloted.aspect = pilotedNode?.aspect || DEFAULT_CAMERA_ASPECT;
      piloted.updateProjectionMatrix();
    }

    // Every *other* camera node keeps its own configured aspect, so its
    // frustum helper reflects reality when viewed from outside — otherwise a
    // camera previously piloted (and left at the container's aspect) would
    // show a stale/incorrect frustum shape until its next unrelated edit.
    // Each camera's own gizmo is also hidden while it's the one piloting the
    // viewport (rendering *through* a camera otherwise puts the near edges of
    // its own frustum lines directly in view) and restored the moment
    // piloting switches away from it.
    for (const n of cameraNodes) {
      const obj = objectsByIdRef.current.get(n._id);
      if (obj?.userData.gizmo)
        obj.userData.gizmo.visible = n._id !== activeCameraId;
      if (n._id === activeCameraId) continue;
      const cam = obj?.userData.camera;
      if (!cam) continue;
      cam.aspect = n.aspect || DEFAULT_CAMERA_ASPECT;
      cam.updateProjectionMatrix();
    }

    transformControls.camera = renderCamera;
    // Switching in/out of piloting changes the sizing mode itself (fill vs.
    // fixed-aspect letterbox) — re-run immediately rather than waiting for
    // the next real container resize to pick it up.
    resizeRef.current?.();

    // Recomputed here (node edits + camera switches) rather than every
    // animation frame like the "AI Objects" overlay below — InspectPanel.jsx
    // just needs a reasonably current preview, not a live-during-fly-through
    // one, and this avoids re-rendering Scene3DEditor 60 times a second for a
    // panel that may not even be the open sidebar tab.
    onObjectBoundsChange?.(
      computeObjectBoundsById({
        nodes,
        objectsById: objectsByIdRef.current,
        camera: renderCamera,
      }),
    );
    onLightBoundsChange?.(
      computeLightBoundsById({
        nodes,
        objectsById: objectsByIdRef.current,
        camera: renderCamera,
      }),
    );
    onEnvironmentInfoChange?.(
      buildEnvironmentInfo({
        nodes,
        objectsById: objectsByIdRef.current,
        camera: renderCamera,
        ambientLightColor,
        ambientLightIntensity,
      }),
    );
  }, [
    activeCameraId,
    nodes,
    cameraNodes,
    onObjectBoundsChange,
    onLightBoundsChange,
    onEnvironmentInfoChange,
    ambientLightColor,
    ambientLightIntensity,
  ]);

  // `containerSize` is the render box computed in `resize` (not the raw
  // panel size) — while piloting, it's already contain-fit to that camera's
  // own aspect, so this overlay is a no-op there (the box already *is* that
  // shape). It only does real work for a camera that's selected in the
  // Properties panel but isn't the one piloting the viewport: a guide
  // showing what that camera would frame, letterboxed/pillarboxed within
  // whatever's actually being rendered (free camera or another scene
  // camera) — so editing its aspect gives immediate feedback either way.
  const pilotedCameraNode = activeCameraId
    ? nodes.find((n) => n._id === activeCameraId)
    : null;
  const selectedCameraNode =
    selectedNodeId &&
    nodes.find((n) => n._id === selectedNodeId && n.type === "camera");
  const safeFrameCameraNode = selectedCameraNode || pilotedCameraNode;
  let safeFrame = null;
  if (safeFrameCameraNode && containerSize.w && containerSize.h) {
    const cameraAspect = safeFrameCameraNode.aspect || DEFAULT_CAMERA_ASPECT;
    const containerAspect = containerSize.w / containerSize.h;
    safeFrame =
      containerAspect > cameraAspect
        ? { width: containerSize.h * cameraAspect, height: containerSize.h }
        : { width: containerSize.w, height: containerSize.w / cameraAspect };
  }

  return (
    <div
      ref={containerRef}
      className="w-full h-full relative overflow-hidden flex items-center justify-center">
      <div className="absolute top-2 left-2 z-10 flex flex-col">
        <Select
          value={activeCameraId || "__free__"}
          onValueChange={(v) => {
            commitFlyTransform();
            setActiveCameraId(v === "__free__" ? null : v);
          }}>
          <SelectTrigger className="inline-flex h-9 outline-none items-center gap-2 px-2 text-xs rounded-md border border-secondary bg-background hover:bg-secondary text-foreground cursor-pointer">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__free__" className="text-xs">
              Free Camera
            </SelectItem>
            {cameraNodes.map((n) => (
              <SelectItem key={n._id} value={n._id} className="text-xs">
                {n.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {safeFrame && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div
            className="border-y border-x-0 border-secondary/60"
            style={{
              width: safeFrame.width,
              height: safeFrame.height,
              boxShadow: "0 0 0 9999px rgb(16,16,21)",
            }}
          />
        </div>
      )}
      {/* AI Objects bounding-box overlay — sized/centered to exactly match
          the WebGL canvas's own render box (`containerSize`, the same
          contain-fit box `safeFrame` above measures itself against), not a
          blanket `inset-0`, since piloting a camera whose aspect doesn't
          match the panel letterboxes that canvas rather than filling it. */}
      <canvas
        ref={aiObjectsOverlayRef}
        className="absolute pointer-events-none"
        style={{
          width: containerSize.w,
          height: containerSize.h,
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
        }}
      />
    </div>
  );
});

export default Viewport;
